import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import { useClassStore } from '../../stores/classesStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useUsersStore } from '../../stores/usersStore'
import { useStudentsStore } from '../../stores/studentsStore'
import { Class, School, User } from '../../types'
import Icon from '../components/Icon'

const Classes: React.FC = () => {
  const navigate = useNavigate()
  const {
    schoolClasses,
    schoolClassesLoading,
    schoolClassesError,
    fetchSchoolClasses,
    removeClass,
  } = useClassStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()
  const { users, loading: usersLoading, fetchUsers } = useUsersStore()
  const { loading: studentsLoading, fetchStudents } = useStudentsStore()

  // Fetch schoolClasses, schools, users, and students on component mount
  useEffect(() => {
    fetchSchoolClasses()
    fetchSchools()
    fetchUsers()
    fetchStudents()
  }, [fetchSchoolClasses, fetchSchools, fetchUsers, fetchStudents])

  const handleDeleteClass = async (id: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeClass(id)
      } catch (err) {
        console.log('Error deleting class:', err)
      }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getSchoolName = (schoolId: string) => {
    const school = schools.find((school: School) => school._id === schoolId)
    return school?.displayName || 'Unknown School'
  }

  const formatTeachers = (
    classTeachers:
      | string[]
      | Array<{ _id: string; firstName: string; lastName: string }>,
  ) => {
    if (classTeachers.length === 0) return 'No teachers assigned'

    // Check if teachers are populated (objects) or just IDs (strings)
    if (typeof classTeachers[0] === 'object') {
      // Teachers are populated, extract names directly
      return (
        classTeachers as Array<{
          _id: string
          firstName: string
          lastName: string
        }>
      )
        .map((teacher) => `${teacher.firstName} ${teacher.lastName}`)
        .join(', ')
    } else {
      // Teachers are just IDs, need to resolve from users store
      const teacherNames = (classTeachers as string[]).map((teacherId) => {
        const teacher = users.find((u: User) => u._id === teacherId)
        return teacher
          ? `${teacher.firstName} ${teacher.lastName}`
          : 'Unknown Teacher'
      })

      return teacherNames.join(', ')
    }
  }

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<Class>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'school',
        header: 'School',
        cell: (info) => {
          const school = info.getValue() as any
          const schoolId = typeof school === 'object' ? school._id : school
          return (
            <div className="text-sm text-gray-900">
              {getSchoolName(schoolId)}
            </div>
          )
        },
      },
      {
        accessorKey: 'students',
        header: 'Students',
        cell: (info) => {
          const students = info.getValue() as any[]
          return (
            <div className="text-sm text-gray-900">
              {students.length} students
            </div>
          )
        },
      },
      {
        accessorKey: 'teachers',
        header: 'Teachers',
        cell: (info) => {
          const teachers = info.getValue() as any[]
          return (
            <div
              className="text-sm text-gray-900"
              title={formatTeachers(teachers)}
            >
              {teachers.length} teacher{teachers.length !== 1 ? 's' : ''}
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium flex">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/manage/classes/${row.original._id}`)
              }}
              variant="link"
              className="mr-3"
            >
              Edit
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClass(row.original._id, row.original.name)
              }}
              variant="link"
              className="text-red-600 hover:text-red-900"
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [schools, users],
  )

  if (
    schoolClassesLoading ||
    schoolsLoading ||
    usersLoading ||
    studentsLoading
  ) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classes</h1>
          <p className="text-gray-600">Manage classes in your schools</p>
        </div>
        <Button
          onClick={() => navigate('/manage/classes/new')}
          variant="primary"
          className="ml-auto flex items-center gap-2"
        >
          <Icon type="plus" />
          Create Class
        </Button>
      </div>

      {/* Error Display */}
      {schoolClassesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {schoolClassesError}
        </div>
      )}

      {/* Classes Table */}
      <DataTable
        data={schoolClasses}
        columns={columns}
        searchPlaceholder="Search classes..."
        emptyStateTitle="No classes"
        emptyStateDescription="Get started by creating your first class."
        emptyStateIcon={
          <Icon type="building" className="mx-auto h-12 w-12 text-gray-400" />
        }
        onCreateClick={() => navigate('/manage/classes/new')}
        createButtonText="Create Class"
        loading={
          schoolClassesLoading ||
          schoolsLoading ||
          usersLoading ||
          studentsLoading
        }
        onRowClick={(classItem) => navigate(`/manage/classes/${classItem._id}`)}
      />
    </div>
  )
}

export default Classes
